import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useStyletron } from 'baseui'
import { setFilters } from '../DashboardV2Slice'
import { DashboardV2Filters } from '../types'

type FilterKey = keyof DashboardV2Filters

const filterMappings: Record<string, { string: string; index: number }[]> = {
  status: [
    { string: 'treated', index: 0 },
    { string: 'nonTreated', index: 1 },
    { string: 'error', index: 2 },
    { string: 'abandonedSelectra', index: 3 },
    { string: 'abandonedProvider', index: 4 },
  ],
  energy: [
    { string: 'elec', index: 0 },
    { string: 'gas', index: 1 },
    { string: 'dual', index: 2 },
    { string: 'propane', index: 3 },
  ],
  productType: [
    { string: 'box', index: 0 },
    { string: 'mobile', index: 1 },
  ],
  productTypeInsurance: [
    { string: 'home', index: 0 },
    { string: 'car', index: 1 },
    { string: 'health', index: 2 },
    { string: 'other', index: 3 },
  ],
  prestation: [
    { string: 'cdf', index: 0 },
    { string: 'mes', index: 1 },
  ],
  prestationInsurance: [
    { string: 'cdf', index: 0 },
    { string: 'new', index: 1 },
  ],
  prestationEnergy: [
    { string: 'cdf', index: 0 },
    { string: 'mes', index: 1 },
    { string: 'fmes', index: 2 },
  ],
  contractType: [
    { string: 'part', index: 0 },
    { string: 'pro', index: 1 },
  ],
  details: [
    { string: 'owned', index: 0 },
  ],
}

const useContractsFilters = ({ filters }: { filters: DashboardV2Filters }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [css, theme] = useStyletron()

  const dispatchFilters = (newFilters: DashboardV2Filters) => {
    dispatch(setFilters(newFilters))
  }

  const mapValues = (value: unknown, filter: string, returnIndex = false): unknown => {
    const mapping = filterMappings[filter]
    if (!mapping) return undefined

    if (typeof value === 'number' || typeof value === 'string') {
      for (const entry of mapping) {
        if (returnIndex && entry.string === value) return [entry.index]
        if (!returnIndex && entry.index === value) return entry.string
        if (!returnIndex && entry.string === value) return entry.string
      }
    } else if (Array.isArray(value)) {
      const result: number[] = []
      if (returnIndex) {
        for (const entry of mapping) {
          if (value.includes(entry.string)) result.push(entry.index)
        }
      }
      return result
    }
    return undefined
  }

  const manageCheckboxGroup = (_event: React.SyntheticEvent, index: number, filter: FilterKey) => {
    const payload = { ...filters }
    const currentValue = filters[filter]
    const mappedValue = mapValues(index, filter) as string

    if (Array.isArray(currentValue)) {
      if (!currentValue.includes(mappedValue)) {
        dispatchFilters({ ...payload, [filter]: [...currentValue, mappedValue] })
      } else {
        dispatchFilters({ ...payload, [filter]: currentValue.filter(v => v !== mappedValue) })
      }
    } else {
      dispatchFilters({ ...payload, [filter]: [mappedValue] })
    }
  }

  const manageRadioGroup = (_event: React.SyntheticEvent, index: number, filter: FilterKey) => {
    const getter = filters[filter]
    const payload = { ...filters }
    const getValue = mapValues(index, filter) as string

    if (getter === getValue) {
      dispatchFilters({ ...payload, [filter]: '' as never })
    } else {
      dispatchFilters({ ...payload, [filter]: getValue as never })
    }
  }

  return {
    t,
    css,
    theme,
    dispatchFilters,
    manageCheckboxGroup,
    manageRadioGroup,
    mapValues,
  }
}

export default useContractsFilters
